import {
  tyreSearchSchema,
  tyreSelectionSchema,
  paginateTyreProducts,
  type TyreProduct,
  type TyreSearch,
  type TyreSearchResult,
  type TyreSelection,
  type TyreQuote,
  type TyreOrderResult,
} from "@autopic/shared";

export type DemoSearchResult = Omit<TyreSearchResult, "environment"> & {
  environment: "demo";
};
export type DemoQuote = Omit<TyreQuote, "environment"> & {
  environment: "demo";
};
export type DemoOrderResult = Omit<TyreOrderResult, "environment"> & {
  environment: "demo";
};

/** Explicit public presentation mode. Never inferred from an API/auth failure. */
export function isTyreDemo(garageId: string, flag: string | null): boolean {
  return garageId === "demo-sandbox" && flag === "true";
}

const sizes = ["2055516", "2254517", "1956515"];
const ranges = ["Budget", "MidRange", "Premium"] as const;
const seasons = ["Summer", "AllSeason", "Winter"] as const;
const pageSize = 4;

function catalogue(): TyreProduct[] {
  const deliveryDate = new Date(Date.now() + 86_400_000)
    .toISOString()
    .slice(0, 10);
  return sizes.flatMap((size) =>
    ranges.flatMap((category, range) =>
      seasons.map((season, index) => ({
        code: `DEMO-${size}-${category}-${season}`,
        description: `${size.slice(0, 3)}/${size.slice(3, 5)} R${size.slice(5)} Sample ${category === "MidRange" ? "Mid-range" : category} ${season === "AllSeason" ? "All-season" : season}`,
        brand: ["Sample Road", "Sample Touring", "Sample Performance"][range]!,
        category,
        season,
        size: `${size.slice(0, 3)}/${size.slice(3, 5)} R${size.slice(5)}`,
        pattern: ["Summer Drive", "Four Seasons", "Winter Grip"][index]!,
        loadSpeed: "91V",
        fuel: ["C", "B", "A"][range]!,
        wetGrip: ["C", "B", "A"][range]!,
        noiseDb: String(72 - range),
        unitPricePence: 5500 + range * 2500 + index * 500,
        stock: 12,
        deliveryDate,
        imageUrl: `/tyres/example-${["lower", "middle", "higher"][range]}.jpg`,
      })),
    ),
  );
}

/** Synthetic catalogue: no supplier requests, credentials or real prices. */
export function searchDemoTyres(input: TyreSearch): DemoSearchResult {
  const search = tyreSearchSchema.parse(input);
  const matches = catalogue().filter(
    (product) =>
      product.code.startsWith(`DEMO-${search.size}-`) &&
      (search.category === "all" || product.category === search.category) &&
      (search.season === "all" || product.season === search.season),
  );
  return {
    environment: "demo",
    ...paginateTyreProducts(
      matches,
      search.page,
      pageSize,
      JSON.stringify([search.size, search.category, search.season]),
    ),
  };
}

export function quoteDemoTyres(input: TyreSelection): DemoQuote {
  const selection = tyreSelectionSchema.parse(input);
  const product = catalogue().find(
    (item) => item.code === selection.productCode,
  );
  if (!product) throw new Error("Choose a tyre from the sample catalogue.");
  const id = crypto.randomUUID();
  return {
    environment: "demo",
    id,
    reference: `DEMO-${id.slice(0, 8).toUpperCase()}`,
    productCode: product.code,
    description: product.description,
    quantity: selection.quantity,
    unitPricePence: product.unitPricePence,
    deliveryDate: product.deliveryDate!,
    tradingPoint: "DEMO",
    expiresAt: new Date(Date.now() + 300_000).toISOString(),
  };
}

/** A local receipt only. It is never persisted or sent to a supplier. */
export function completeDemoQuote(quote: DemoQuote): DemoOrderResult {
  const netPence = quote.unitPricePence * quote.quantity;
  return {
    environment: "demo",
    reference: quote.reference,
    bondOrderNumber: quote.reference,
    description: quote.description,
    quantity: quote.quantity,
    netPence,
    vatPence: Math.round(netPence * 0.2),
    deliveryDate: quote.deliveryDate,
    deliveryDateChanged: false,
  };
}
